import { geoDist } from './geo';
// Follow-Me tracks: profiles, stats, GPX
export const TRACK_PROFILES = {
  gas: { icon: '🚗', color: '#f59e0b', minStep: 8 },
  hike: { icon: '🥾', color: '#16a34a', minStep: 3 },
  leak: { icon: '🔍', color: '#dc2626', minStep: 2 },
  bike: { icon: '🚲', color: '#2563eb', minStep: 5 },
};
export const hav = (a, b) => geoDist([a.lat, a.lon], [b.lat, b.lon]);

// length, ascent/descent (3 m hysteresis), moving time (pauses excluded), avg speed
export function trackStats(tr) {
  const pts = tr.pts || [];
  let len = 0, up = 0, down = 0, ref = null;
  for (let i = 1; i < pts.length; i++) if (!pts[i].brk) len += hav(pts[i - 1], pts[i]);
  pts.forEach((p) => {
    if (p.alt == null) return;
    if (ref == null) { ref = p.alt; return; }
    const d = p.alt - ref;
    if (d >= 3) { up += d; ref = p.alt; } else if (d <= -3) { down -= d; ref = p.alt; }
  });
  const start = tr.start || pts[0]?.t, end = tr.end || pts[pts.length - 1]?.t || start;
  const moving = Math.max(0, (end - start) - (tr.paused || 0));
  const alts = pts.map((p) => p.alt).filter((a) => a != null);
  return { km: len / 1000, up, down, start, end, ms: moving, kmh: moving > 0 ? (len / 1000) / (moving / 3.6e6) : 0, min: alts.length ? Math.min(...alts) : null, max: alts.length ? Math.max(...alts) : null, n: pts.length };
}

// slope-coloured polylines: green < 3 %, yellow 3-8 %, red > 8 % (grade over >= 15 m chunks)
export const SLOPE_COLORS = { g: '#16a34a', y: '#eab308', r: '#dc2626' };
export const slopeKey = (pct) => (pct == null ? null : pct < 3 ? 'g' : pct <= 8 ? 'y' : 'r');
export function slopeSegs(pts, minLen = 15) {
  const out = [];
  const push = (ln, k) => { const last = out[out.length - 1]; if (last && last.k === k && last.open) last.ln.push(...ln.slice(1)); else out.push({ ln, k, open: true }); };
  const runs = (pts || []).reduce((a, p) => { if (p.brk || !a.length) a.push([]); a[a.length - 1].push(p); return a; }, []).filter((r) => r.length > 1);
  runs.forEach((r) => {
    if (out.length) out[out.length - 1].open = false;
    let s = 0, d = 0;
    for (let i = 1; i < r.length; i++) {
      d += hav(r[i - 1], r[i]);
      if (d < minLen && i < r.length - 1) continue;
      const a = r[s].alt, b = r[i].alt;
      push(r.slice(s, i + 1).map((p) => [p.lat, p.lon]), a != null && b != null && d > 0 ? slopeKey((Math.abs(b - a) / d) * 100) : null);
      s = i; d = 0;
    }
  });
  return out.map(({ ln, k }) => ({ ln, k }));
}
export const hasAlt = (pts) => (pts || []).filter((p) => p.alt != null).length > 1;

export const fmtDur = (ms) => { const m = Math.round(ms / 60000); return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`; };
const x = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const gpxWpts = (tr, t, photos) => (tr.wps || []).map((w) => { const n = photos?.(w) || 0; return `<wpt lat="${w.lat.toFixed(7)}" lon="${w.lon.toFixed(7)}">${w.alt != null ? `<ele>${(+w.alt).toFixed(1)}</ele>` : ''}<time>${new Date(w.t).toISOString()}</time><name>${x(wpRef(tr, w))} ${x(t ? wpLabel(t, w) : w.kind)}</name>${w.note || n ? `<desc>${x([w.note, n ? `📷 ${n}` : ''].filter(Boolean).join(' · '))}</desc>` : ''}${n ? '<sym>Photo</sym>' : ''}<type>${x(w.kind)}</type></wpt>`; }).join('');
const gpxTrk = (tr, profileLabel) => {
  const segs = [];
  (tr.pts || []).forEach((p, i) => { if (!i || p.brk) segs.push([]); segs[segs.length - 1].push(p); });
  const seg = (s) => `<trkseg>${s.map((p) => `<trkpt lat="${p.lat.toFixed(7)}" lon="${p.lon.toFixed(7)}">${p.alt != null ? `<ele>${p.alt.toFixed(1)}</ele>` : ''}<time>${new Date(p.t).toISOString()}</time></trkpt>`).join('')}</trkseg>`;
  return `<trk><name>${x(tr.name)}</name><type>${x(profileLabel)}</type>${segs.map(seg).join('')}</trk>`;
};
const gpxDoc = (name, time, body) => `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" creator="GISS FieldRapport Pro" xmlns="http://www.topografix.com/GPX/1/1"><metadata><name>${x(name)}</name><time>${new Date(time || Date.now()).toISOString()}</time></metadata>${body}</gpx>`;
// photos(wp) -> number of photos at a waypoint (optional)
export function trackGpx(tr, profileLabel, t, photos) {
  return gpxDoc(tr.name, tr.start, gpxWpts(tr, t, photos) + gpxTrk(tr, profileLabel));
}
// all tours in one file: all waypoints first (GPX 1.1 order), then one <trk> per tour
export function allTracksGpx(tracks, t, name, photos) {
  return gpxDoc(name, tracks[0]?.start, tracks.map((tr) => gpxWpts(tr, t, photos)).join('') + tracks.map((tr) => gpxTrk(tr, t['trkP_' + tr.profile])).join(''));
}
// phone share sheet (Android/iOS) with the .gpx file, otherwise download
export async function shareGpx(txt, name) {
  const file = new File([txt], name, { type: 'application/gpx+xml' });
  if (navigator.canShare?.({ files: [file] })) { try { await navigator.share({ files: [file], title: name }); return; } catch (e) { if (e?.name === 'AbortError') return; } }
  downloadText(txt, name, 'application/gpx+xml');
}
export const downloadText = (txt, name, type) => { const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([txt], { type })); a.download = name; a.click(); };
export const trackFile = (tr) => (tr.name || 'route').replace(/[^\w-]+/g, '_').slice(0, 40);

// on-route checkpoints
export const WP_KINDS = {
  fit: { icon: '🔵', color: '#2563eb', st: ['ok', 'maint', 'defect'] },
  gas: { icon: '🟡', color: '#eab308', st: ['checked', 'measOk', 'anomaly'] },
  leak: { icon: '🔴', color: '#dc2626', st: [] },
  note: { icon: '🟢', color: '#16a34a', st: [] },
  woodPile: { icon: '🪵', color: '#92400e', st: [], fields: { species: 'text', sort: 'text', grade: 'text', len: 'num', dia: 'num', count: 'num', volume: 'num' } },
  tree: { icon: '🌳', color: '#15803d', st: ['healthy', 'weak', 'dead'], fields: { species: 'text', girth: 'num', fell: 'check' } },
  damage: { icon: '🪲', color: '#b45309', st: ['game', 'beetle', 'storm'] },
};
const wpData = (t, wp) => Object.entries(wp.data || {}).filter(([, v]) => v !== '' && v != null && v !== false).map(([k, v]) => (v === true ? t['wpF_' + k] : `${t['wpF_' + k]}: ${v}`)).join(' · ');
export const wpRef = (tr, wp) => `R${tr.no || 1}-P${wp.no}`;
export const wpLabel = (t, wp) => { const d = wpData(t, wp); return `${t['wpK_' + wp.kind]}${wp.status ? ` – ${t['wpS_' + wp.status]}` : ''}${d ? ` (${d})` : ''}`; };
